import { PlanBadge } from '@/components/billing/PlanBadge';
import {
  List,
  Moon,
  Plus,
  SidebarSimple,
  SignOut,
  Sun,
  UserCircle,
  Users,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Tooltip } from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { useApp } from '@/hooks/useApp';
import { useAuth } from '@/hooks/useAuth';
import { authApi } from '@/api/endpoints';

interface Props {
  isMobile: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  drawerOpen: boolean;
  onDrawerChange: (open: boolean) => void;
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export function Topbar({
  isMobile,
  collapsed,
  onToggleCollapse,
  drawerOpen,
  onDrawerChange,
}: Props) {
  const navigate = useNavigate();
  const { themeMode, toggleTheme } = useApp();
  const { user, signOut } = useAuth();

  const logout = async () => {
    authApi.logout().catch(() => undefined);
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-2 border-b border-border bg-card px-2 shadow-xs sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
          {isMobile ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDrawerChange(true)}
              aria-label="Open navigation"
            >
              <List size={18} />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <SidebarSimple size={18} />
            </Button>
          )}

          <WorkspaceSwitcher />
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <div className="hidden shrink-0 md:block">
            <PlanBadge />
          </div>

          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/app/posts/new')}
            className="gap-1 font-medium"
          >
            <Plus size={16} />
            {!isMobile && 'New post'}
          </Button>

          <Tooltip title={themeMode === 'light' ? 'Dark theme' : 'Light theme'}>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {themeMode === 'light' ? <Moon size={17} /> : <Sun size={17} />}
            </Button>
          </Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer">
                <Avatar
                  size="sm"
                  className="bg-primary text-primary-foreground"
                >
                  <AvatarFallback>
                    {user ? initials(user.fullName || user.email) : '?'}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/app/settings/account')}
              >
                <UserCircle size={16} />
                <span>Account settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/app/settings/members')}
              >
                <Users size={16} />
                <span>Members</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} danger>
                <SignOut size={16} />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <Sheet open={drawerOpen} onOpenChange={onDrawerChange}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar onNavigate={() => onDrawerChange(false)} />
        </SheetContent>
      </Sheet>

    </>
  );
}
